import { Coordinates } from '../components/declarations/PositionInfo';
import { config } from '../config/config';
import { isMobileOrTablet } from './features';
import { ResizerDimensions, dimensions } from './resizer';
import { clamp } from './tools';
import gsap from 'gsap';

import { Application, Assets, FederatedPointerEvent, Rectangle, Sprite } from 'pixi.js';

const INNER_VELOCITY = 8;
const MIDDLE_VELOCITY = 4;
const OUTER_VELOCITY = 2;

const INNER_BORDER_PERCENTAGE = 0.075;
const MIDDLE_BORDER_PERCENTAGE = 0.15;
const OUTER_BORDER_PERCENTAGE = 0.25;
const MOVE_EASE = 0.1;

export default class Input {
  public velocity: Coordinates;
  private easedVelocity: Coordinates;
  private stageRatio: Coordinates;
  private isStopped: boolean;
  private isInitialized: boolean;
  private isDragging: boolean = false;
  public isDraggingStarted: boolean = false;

  private layerSize: Coordinates = { x: 0, y: 0 };
  private dragStartPosition: Coordinates = { x: 0, y: 0 };
  private dragStartLayerPosition: Coordinates = { x: 0, y: 0 };
  private velocityArrowLeft?: Sprite;
  private velocityArrowRight?: Sprite;
  private pixi?: Application;

  constructor() {
    this.velocity = { x: 0, y: 0 };
    this.easedVelocity = { x: 0, y: 0 };

    // stage percentage position  -0.5 -> 0.5
    this.stageRatio = {
      x: config.INIT_POS_X,
      y: config.INIT_POS_Y,
    };
    this.isStopped = true;
    this.isInitialized = false;

    this.initializeVelocityArrows();
  }

  init(pixi: Application, isTouch = true) {
    if (!isTouch) {
      pixi.ticker.add((deltaTime) => this.updateRatioFromEasedVelocity(deltaTime));
      pixi.ticker.add(() => this.updateVelocityArrowState());
      pixi.stage.on('globalmousemove', (event) => this.onPointerMove(event));
      // pixi.stage.on('pointerdown', (event) => this.onPointerMove(event));
    } else {
      //touch
      pixi.stage.on('pointerdown', (event) => this.onDragStart(event));
      pixi.stage.on('pointerup', () => this.onDragEnd());
      pixi.stage.on('pointerupoutside', () => this.onDragEnd());
      pixi.stage.on('pointermove', (event) => this.onDragMove(event));
    }

    this.layerSize = config.LAYER_DIMENSIONS[2]; // todo param layerSize
    this.isInitialized = true;
    this.pixi = pixi;
  }

  stop() {
    this.isStopped = true;
    this.velocity.x = 0;
    this.velocity.y = 0;
    this.easedVelocity.x = 0;
    this.easedVelocity.y = 0;

    this.isDragging = false;
  }

  pause() {
    this.velocity.x = 0;
    this.velocity.y = 0;
    this.easedVelocity.x = 0;
    this.easedVelocity.y = 0;
  }

  start() {
    this.isStopped = false;
  }

  getRatio() {
    if (!this.isInitialized) {
      console.warn('input is not initialized');
    }

    return this.stageRatio;
  }

  onPointerMove(e: FederatedPointerEvent) {
    if (e.global.y < 0 || e.global.y > dimensions.containerH) {
      this.velocity.x = 0;
      return;
    }
    // //1. linear
    //const x = e.data.global.x / dimensions.containerW; // 0-1 linear
    //
    // //2. linear with border in pixels (scaled) X_BORDER in pixels
    // const x = (e.data.global.x - X_BORDER * dimensions.scale) / (dimensions.containerW - 2 * X_BORDER * dimensions.scale);
    //
    // //-0.5 - 0.5 not eased
    // stageRatio.x = Math.min(Math.max(x, 0), 1) - 0.5;
    // stageRatio.y = Math.min(Math.max(e.data.global.y / dimensions.containerH, 0), 1) - 0.5;

    //3. velocity / border
    if (
      Math.abs(e.global.x - dimensions.containerW / 2) >
        (dimensions.containerW / 2) * (1 - INNER_BORDER_PERCENTAGE * 2) &&
      Math.abs(e.global.x - dimensions.containerW / 2) < dimensions.containerW / 2
    ) {
      this.velocity.x = e.global.x - dimensions.containerW / 2 > 0 ? INNER_VELOCITY : -INNER_VELOCITY;
    } else if (
      Math.abs(e.global.x - dimensions.containerW / 2) >
        (dimensions.containerW / 2) * (1 - MIDDLE_BORDER_PERCENTAGE * 2) &&
      Math.abs(e.global.x - dimensions.containerW / 2) < dimensions.containerW / 2
    ) {
      this.velocity.x = e.global.x - dimensions.containerW / 2 > 0 ? MIDDLE_VELOCITY : -MIDDLE_VELOCITY;
    } else if (
      Math.abs(e.global.x - dimensions.containerW / 2) >
        (dimensions.containerW / 2) * (1 - OUTER_BORDER_PERCENTAGE * 2) &&
      Math.abs(e.global.x - dimensions.containerW / 2) < dimensions.containerW / 2
    ) {
      this.velocity.x = e.global.x - dimensions.containerW / 2 > 0 ? OUTER_VELOCITY : -OUTER_VELOCITY;
    } else {
      this.velocity.x = 0;
    }

    if (this.pixi) {
      if (this.velocity.x !== 0 && this.stageRatio.x !== -0.5 && this.stageRatio.x !== 0.5) {
        this.pixi.stage.interactiveChildren = false;
      } else {
        this.pixi.stage.interactiveChildren = true;
      }
    }
  }

  initializeVelocityArrows = async () => {
    const textureLeft = await Assets.load(`${config.IMAGES_PATH}icons/ArrowLeft.png`);
    const textureRight = await Assets.load(`${config.IMAGES_PATH}icons/ArrowRight.png`);
    this.velocityArrowLeft = Sprite.from(textureLeft);
    this.velocityArrowRight = Sprite.from(textureRight);
    this.velocityArrowLeft.x = 30;
    this.velocityArrowRight.x =
      (dimensions.containerW - (30 + this.velocityArrowRight.width) * dimensions.scale) / dimensions.scale;
    if (document.body.classList.contains('inside')) {
      this.velocityArrowLeft.y =
        0.9 * ((dimensions.containerH / 2 - (this.velocityArrowLeft.height / 2) * dimensions.scale) / dimensions.scale);

      this.velocityArrowRight.y =
        0.9 * ((dimensions.containerH / 2 - (this.velocityArrowLeft.height / 2) * dimensions.scale) / dimensions.scale);
    } else {
      this.velocityArrowLeft.y =
        (dimensions.containerH / 2 - (this.velocityArrowLeft.height / 2) * dimensions.scale) / dimensions.scale;

      this.velocityArrowRight.y =
        (dimensions.containerH / 2 - (this.velocityArrowLeft.height / 2) * dimensions.scale) / dimensions.scale;
    }

    if (isMobileOrTablet) {
      this.velocityArrowLeft.zIndex = 4;
      this.velocityArrowLeft.hitArea = new Rectangle(
        -this.velocityArrowLeft.width / 2,
        -this.velocityArrowLeft.height / 2,
        this.velocityArrowLeft.width * 2,
        this.velocityArrowLeft.height * 2,
      ) as Rectangle;

      this.velocityArrowRight.zIndex = 4;
      this.velocityArrowRight.hitArea = new Rectangle(
        -this.velocityArrowRight.width / 2,
        -this.velocityArrowRight.height / 2,
        this.velocityArrowRight.width * 2,
        this.velocityArrowRight.height * 2,
      ) as Rectangle;
      console.log(this.velocityArrowLeft);
    }

    this.pixi?.stage.addChild(this.velocityArrowLeft);
    this.pixi?.stage.addChild(this.velocityArrowRight);

    if (isMobileOrTablet) {
      this.velocityArrowLeft.alpha = 0.8;
      this.velocityArrowLeft.scale = { x: 0.8, y: 0.8 };
      this.velocityArrowRight.alpha = 0.8;
      this.velocityArrowRight.scale = { x: 0.8, y: 0.8 };
      this.velocityArrowLeft.eventMode = 'static';
      this.velocityArrowRight.eventMode = 'static';
      this.velocityArrowLeft.on('pointerdown', () => {
        console.log('eh?');
        if (this.velocityArrowLeft) {
          this.velocityArrowLeft.alpha = 1;
          this.velocityArrowLeft.scale = { x: 0.85, y: 0.85 };
        }
      });
      this.velocityArrowLeft.on('pointerup', () => {
        if (this.velocityArrowLeft) {
          this.velocityArrowLeft.alpha = 0.8;
          this.velocityArrowLeft.scale = { x: 0.8, y: 0.8 };
        }
        this.tweenToRatio({ x: this.stageRatio.x - 0.5, y: this.stageRatio.y });
      });
      this.velocityArrowRight.on('pointerdown', () => {
        if (this.velocityArrowRight) {
          this.velocityArrowRight.alpha = 1;
          this.velocityArrowRight.scale = { x: 0.85, y: 0.85 };
        }
      });
      this.velocityArrowRight.on('pointerup', () => {
        if (this.velocityArrowRight) {
          this.velocityArrowRight.alpha = 0.8;
          this.velocityArrowRight.scale = { x: 0.8, y: 0.8 };
        }
        this.tweenToRatio({ x: this.stageRatio.x + 0.5, y: this.stageRatio.y });
      });
    }
  };

  public onResize = (resizerDimensions: ResizerDimensions) => {
    if (this.velocityArrowLeft && this.velocityArrowRight) {
      this.velocityArrowLeft.x = 30;
      this.velocityArrowRight.x =
        (resizerDimensions.containerW - (30 + this.velocityArrowRight.width) * resizerDimensions.scale) /
        resizerDimensions.scale;

      if (document.body.classList.contains('inside')) {
        this.velocityArrowLeft.y =
          0.9 *
          ((resizerDimensions.containerH / 2 - (this.velocityArrowLeft.height / 2) * resizerDimensions.scale) /
            resizerDimensions.scale);

        this.velocityArrowRight.y =
          0.9 *
          ((resizerDimensions.containerH / 2 - (this.velocityArrowLeft.height / 2) * resizerDimensions.scale) /
            resizerDimensions.scale);
      } else {
        this.velocityArrowLeft.y =
          (resizerDimensions.containerH / 2 - (this.velocityArrowLeft.height / 2) * resizerDimensions.scale) /
          resizerDimensions.scale;

        this.velocityArrowRight.y =
          (resizerDimensions.containerH / 2 - (this.velocityArrowLeft.height / 2) * resizerDimensions.scale) /
          resizerDimensions.scale;
      }
    }
  };

  updateVelocityArrowState = () => {
    if (!isMobileOrTablet) {
      if (this.velocityArrowLeft && this.velocityArrowRight) {
        if (this.stageRatio.x === -0.5 || this.stageRatio.x === 0.5) {
          this.velocityArrowLeft.alpha = 0.8;
          this.velocityArrowRight.alpha = 0.8;
          this.velocityArrowRight.scale = { x: 0.8, y: 0.8 };
          this.velocityArrowLeft.scale = { x: 0.8, y: 0.8 };
          return;
        }
        if (this.velocity.x > 0) {
          this.velocityArrowRight.alpha = 1;
          this.velocityArrowRight.scale = { x: 0.85, y: 0.85 };
        } else if (this.velocity.x < 0) {
          this.velocityArrowLeft.alpha = 1;
          this.velocityArrowLeft.scale = { x: 0.85, y: 0.85 };
        } else {
          this.velocityArrowLeft.alpha = 0.8;
          this.velocityArrowRight.alpha = 0.8;
          this.velocityArrowRight.scale = { x: 0.8, y: 0.8 };
          this.velocityArrowLeft.scale = { x: 0.8, y: 0.8 };
        }
      }
    }
  };

  updateRatioFromEasedVelocity = (deltaTime: number) => {
    if (!this.isStopped) {
      this.easedVelocity.x += (this.velocity.x - this.easedVelocity.x) * MOVE_EASE;
      this.easedVelocity.y += (this.velocity.y - this.easedVelocity.y) * MOVE_EASE;

      this.stageRatio.x = clamp(this.stageRatio.x + (this.easedVelocity.x * deltaTime) / 1000, -0.5, 0.5);
      this.stageRatio.y = clamp(this.stageRatio.y + (this.easedVelocity.y * deltaTime) / 1000, -0.5, 0.5);
    }
  };

  tweenToRatio(ratio: Coordinates, duration = 1, startDelay = 0) {
    let ratioX = ratio.x;
    if (ratio.x < -0.5) {
      ratioX = -0.5;
    }
    if (ratio.x > 0.5) {
      ratioX = 0.5;
    }
    gsap.to(this.stageRatio, {
      duration: duration,
      delay: startDelay || 0,
      x: ratioX,
      y: ratio.y,
    });
  }

  onDragStart = (event: FederatedPointerEvent) => {
    if (this.isStopped) {
      return;
    }

    this.isDragging = true;
    this.dragStartPosition = { x: event.global.x, y: event.global.y };
    this.dragStartLayerPosition = this.ratioToPosition(this.stageRatio);
  };

  onDragMove = (event: FederatedPointerEvent) => {
    if (this.isDragging) {
      const dragPositionDiff = {
        x: event.global.x - this.dragStartPosition.x,
        y: event.global.y - this.dragStartPosition.y,
      };

      if (Math.abs(dragPositionDiff.x) + Math.abs(dragPositionDiff.y) > 10) {
        this.isDraggingStarted = true;
      }

      const newLayerPosition = {
        x: this.dragStartLayerPosition.x + dragPositionDiff.x * 2,
        y: this.dragStartLayerPosition.y + dragPositionDiff.y * 2,
      };
      this.stageRatio = this.positionToRatio(newLayerPosition);
    }
  };

  onDragEnd = () => {
    this.isDragging = false;
    this.isDraggingStarted = false;
  };

  ratioToPosition(ratio: Coordinates) {
    return {
      x: -(this.layerSize.x - config.SIZES.MAX_WIDTH) * ratio.x,
      y: -(this.layerSize.y - config.SIZES.MAX_HEIGHT) * ratio.y,
    };
  }

  positionToRatio(position: Coordinates) {
    return {
      x:
        this.layerSize.x - config.SIZES.MAX_WIDTH === 0
          ? 0
          : clamp(-position.x / (this.layerSize.x - config.SIZES.MAX_WIDTH), -0.5, 0.5),
      y:
        this.layerSize.y - config.SIZES.MAX_HEIGHT === 0
          ? 0
          : clamp(-position.y / (this.layerSize.y - config.SIZES.MAX_HEIGHT), -0.5, 0.5),
    };
  }
}
